/**
 * Bundled-fixture loader for the agent-tool-execution dashboard.
 *
 * Issue #2128: instead of fetching `/data/*.json` over HTTP at runtime,
 * the React app imports the same canonical JSON files the Python
 * spinout uses (`tooling/contrib/DifferentialSecurity/agent-tool-execution/`)
 * directly via relative paths. No copies, no Python dev server. The
 * two small derived views — bridge footprint and the delta-diff
 * example — are pure functions of those inputs and live in this hook.
 */

import bridgesJson from "../../../../../../tooling/contrib/DifferentialSecurity/agent-tool-execution/examples/bridges.json" with { type: "json" };
import matrixJson from "../../../../../../tooling/contrib/DifferentialSecurity/agent-tool-execution/matrix/bridge-matrix.json" with { type: "json" };
import ledgerJson from "../../../../../../tooling/contrib/DifferentialSecurity/agent-tool-execution/receipts/LEDGER.template.json" with { type: "json" };
import regionR1Json from "../../../../../../tooling/contrib/DifferentialSecurity/agent-tool-execution/policies/regions/region-r1.json" with { type: "json" };
import regionR2Json from "../../../../../../tooling/contrib/DifferentialSecurity/agent-tool-execution/policies/regions/region-r2.json" with { type: "json" };
import regionR3Json from "../../../../../../tooling/contrib/DifferentialSecurity/agent-tool-execution/policies/regions/region-r3.json" with { type: "json" };
import missingBridgeJson from "../../../../../../tooling/contrib/DifferentialSecurity/agent-tool-execution/examples/missing-bridge.json" with { type: "json" };
import manifestJson from "../../../../../../tooling/contrib/DifferentialSecurity/agent-tool-execution/MANIFEST.json" with { type: "json" };
import { DEMO_PROOF_TREE } from "../proofTree";
import type { Result } from "../lib";
import {
  guardBridges,
  guardFootprint,
  guardLedger,
  guardMatrix,
  guardMissingBridge,
  guardProofTree,
  guardReceipt,
  guardRegion,
  type Bridge,
  type BridgeMatrix,
  type DeltaDiff,
  type Footprint,
  type GeneratorCoordinates,
  type Ledger,
  type ProofTree,
  type Receipt,
  type Region,
} from "../types";

export interface DashboardData {
  bridges: Bridge[] | null;
  matrix: BridgeMatrix | null;
  footprint: Footprint | null;
  ledger: Ledger | null;
  deltaDiff: DeltaDiff | null;
  regions: Region[];
  receipt: Receipt | null;
  proofTree: ProofTree | null;
  missingBridge: Bridge | null;
  errors: string[];
  /** Always `false` — kept on the type for backwards compatibility with consumers that still gate on it. */
  loading: false;
}

/** Tally `generator` ids per axis. Matches Python's `tooling/compute_footprint.py`. */
function computeFootprint(bridges: readonly Bridge[]): GeneratorCoordinates {
  const coords: GeneratorCoordinates = { G1: 0, G2: 0, G3: 0, G4: 0 };
  for (const b of bridges) {
    if (b.generator && b.generator in coords) {
      coords[b.generator]++;
    }
  }
  return coords;
}

/** Compute the override delta — bridges + missing-bridge — directly from the catalog.
 *  Matches Python's `tooling/delta_diff.py` on (added, removed, rule_changes, footprints). */
function computeDeltaDiff(before: readonly Bridge[], added: Bridge): DeltaDiff {
  const beforeIds = new Set(before.map((b) => b.bridge_id));
  const after = beforeIds.has(added.bridge_id)
    ? [...before]
    : [...before, added];
  const addedIds =
    added.bridge_id && !beforeIds.has(added.bridge_id) ? [added.bridge_id] : [];
  const footprintA = computeFootprint(before);
  const footprintB = computeFootprint(after);
  return {
    added_bridges: addedIds,
    removed_bridges: [],
    rule_changes: addedIds.map((bid) => ({
      lhs: bid,
      rhs: "G1.HTTP_GET",
      from: null,
      to: "allowed",
    })),
    footprint_A: footprintA,
    footprint_B: footprintB,
    footprint_delta: {
      G1: footprintB.G1 - footprintA.G1,
      G2: footprintB.G2 - footprintA.G2,
      G3: footprintB.G3 - footprintA.G3,
      G4: footprintB.G4 - footprintA.G4,
    },
  };
}

/** Apply a guard, push any error onto `errors`, and surface the parsed value or null. */
function take<T>(label: string, result: Result<T>, errors: string[]): T | null {
  if (result.ok) return result.value;
  errors.push(`${label}: ${result.error}`);
  return null;
}

function buildDashboardData(): DashboardData {
  const errors: string[] = [];
  const regions: Region[] = [];
  for (const [label, json] of [
    ["region-r1", regionR1Json],
    ["region-r2", regionR2Json],
    ["region-r3", regionR3Json],
  ] as const) {
    const parsed = take(label, guardRegion(json), errors);
    if (parsed) regions.push(parsed);
  }

  const bridges = take("bridges", guardBridges(bridgesJson), errors);
  const missingBridge = take(
    "missing-bridge",
    guardMissingBridge(missingBridgeJson),
    errors,
  );

  const footprint = bridges
    ? take(
        "footprint",
        guardFootprint({
          system_id: "agent_tool_exec_demo",
          coordinates: computeFootprint(bridges),
        }),
        errors,
      )
    : null;

  const deltaDiff =
    bridges && missingBridge ? computeDeltaDiff(bridges, missingBridge) : null;

  return Object.freeze({
    bridges,
    matrix: take("matrix", guardMatrix(matrixJson), errors),
    footprint,
    ledger: take("ledger", guardLedger(ledgerJson), errors),
    deltaDiff,
    regions,
    receipt: take("manifest", guardReceipt(manifestJson), errors),
    proofTree: take("proof-tree", guardProofTree(DEMO_PROOF_TREE), errors),
    missingBridge,
    errors,
    loading: false,
  });
}

const DASHBOARD_DATA: DashboardData = buildDashboardData();

export function useData(): DashboardData {
  return DASHBOARD_DATA;
}

// Re-exports for tests.
export { computeFootprint, computeDeltaDiff };
